import { useState } from "react";
import Grid from "@mui/material/Grid2";
import { Divider } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import Typography from "@common/components/Typography";
import CopyLink from "@common/components/Button/CopyLink";
import { useTranslation } from "react-i18next";
import { BreadcrumbProps } from "./types";
import { useBreadcrumb } from "./hooks";
import Button from "@common/components/Button";
import QRCodeLink from "@common/components/Button/QRCodeLink";
import BackModal from "./BackModal";

/**
 *
 * If we do not want to maintain a history stack ourselves, the previous page has to be kept alive either through
 * the location state, or through search params.
 * Right now, this back button functions through the location state.
 */
const Breadcrumb = (props: BreadcrumbProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const contextualProps = useBreadcrumb();
  const [open, setOpen] = useState(false);
  const state =
    (location.state as {
      previous?: string;
      referrer?: string;
      tab?: string;
    }) ?? {};
  const {
    title = "",
    label,
    componentId,
    copyUrl = true,
    onBack = navigate,
    ignoreLocalization = true,
    previous = state.previous,
    withConfirmationModal = false,
  } = { ...contextualProps, ...props };
  const handleClick = () => {
    // If this page has not been loaded with a previous state in mind (direct link to this detail view for instance)
    // The back button sends back to the index
    if (state.previous !== undefined) onBack(-1);
    else onBack("/");
  };
  return (
    <Grid
      container
      wrap="nowrap"
      direction="row"
      sx={{
        alignItems: "center",
        width: "fit-content",
        gap: "1rem",
      }}
    >
      {previous && (
        <Button
          onClick={withConfirmationModal ? () => setOpen(true) : handleClick}
          variant="outlined"
          startIcon={<ChevronLeft />}
          sx={(theme) => ({
            color: theme.palette.common.grey.gBFBFBF,
            borderColor: theme.palette.common.grey.gBFBFBF,
            maxWidth: "20rem",
          })}
          ignoreLocalization={ignoreLocalization}
          TypographyProps={{
            sx: { fontSize: "0.8rem" },
          }}
          tooltip={previous}
          data-testid="breadcrumb-back-button"
        >
          {previous}
        </Button>
      )}
      {title && (
        <Typography
          tooltip={title}
          sx={(theme) => ({
            color: label
              ? theme.palette.common.grey.gBFBFBF
              : theme.palette.common.white,
            variant: "caption",
            alignSelf: "center",
            textAlign: "center",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            maxWidth: "30rem",
          })}
          data-testid="breadcrumb-title"
        >
          {title}
        </Typography>
      )}
      {label && (
        <>
          {title && <Divider flexItem orientation="vertical" />}

          <Typography
            variant="caption"
            sx={{
              maxWidth: "20rem",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              alignSelf: "center",
              textAlign: "center",
            }}
            tooltip={
              ignoreLocalization ? label.toUpperCase() : t(label).toUpperCase()
            }
          >
            {ignoreLocalization ? label.toUpperCase() : t(label).toUpperCase()}
          </Typography>
        </>
      )}
      {copyUrl && <CopyLink />}
      {componentId && <QRCodeLink componentId={componentId} />}
      <BackModal
        open={open}
        onConfirm={() => {
          handleClick();
          setOpen(false);
        }}
        onCancel={() => setOpen(false)}
      />
    </Grid>
  );
};

export default Breadcrumb;
